class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector(".search-field__input");
    this.predictiveSearchResults = this.querySelector(
      ".predictive-search__content"
    );
    this.searchResultsWrapper = this.querySelector(
      ".predictive-search__content-wrapper"
    );
    this.defaultText = this.searchResultsWrapper.innerHTML;
    this.search_box = this.querySelector(".search-box ");
    this.search_button = document.querySelector(".search-button");
    this.close_search_button = this.querySelector(".search__button-toggle");
    this.modal_overlay = this.querySelector(".modal-overlay");
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.modal_overlay) {
      this.modal_overlay.addEventListener("click", () => {
        this.closeSearch();
      });
    }
    this.close_search_button.addEventListener("click", () => {
      this.input.value = "";
      this.searchResultsWrapper.innerHTML = this.defaultText;
      this.closeSearch();
    });

    this.search_button.addEventListener("click", () => {
      this.openSearch();
    });

    this.search_button.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.openSearch();
      }
    });

    this.openSearch = () => {
      this.open();
      this.search_box.style.visibility = "visible";
      this.search_box.style.display = "inline-flex";
      this.search_box.style.opacity = "1";
      this.search_box.classList.add("open");
      document.body.classList.add("overflow-hidden");
      this.input.focus({ focusVisible: true });
      this.topSearches = this.querySelectorAll("p.top-search__term");
      Array.from(this.topSearches).forEach((searchTerm) => {
        searchTerm.addEventListener("click", (e) => {
          this.input.value = e.target.innerText;
          this.input.focus();
          this.input.dispatchEvent(new Event("searchInputChange"));
        });
      });
    };

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 800).bind(this)
    );
    this.input.addEventListener("searchInputChange", () => {
      this.onChange();
    });
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  closeSearch() {
    this.search_box.style.visibility = "hidden";
    this.search_box.style.opacity = "0";
    this.search_box.classList.remove("open");
    document.body.classList.remove("overflow-hidden");
    this.input.blur();
  }

  getQuery() {
    return this.input.value.trim();
  }

  getResultCount() {
    this.resultsList = this.searchResultsWrapper.querySelector(
      ".predictive-search__results-list"
    );
    return {
      result_count: this.searchResultsWrapper.querySelectorAll(
        ".predictive-search__item"
      ).length,
      product_count: this.resultsList.dataset.productsCount,
      page_count: this.resultsList.dataset.pagesCount,
    };
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;
    const moveUp = direction === "up";
    const allItems = Array.from(
      this.querySelectorAll('.predictive-search__list-item:not(.search-term)')
    );
    if (!allItems.length) return;
    const selectedLink = this.querySelector('[aria-selected="true"]');
    const selectedItem = selectedLink ? selectedLink.closest("li") : null;
    let newItem;
    if (!selectedItem && !moveUp) {
      newItem = allItems[0];
    } else if (moveUp) {
      const index = allItems.indexOf(selectedItem);
      newItem = allItems[index > 0 ? index - 1 : allItems.length - 1];
    } else {
      const index = allItems.indexOf(selectedItem);
      newItem = allItems[(index + 1) % allItems.length];
    }
    if (selectedLink) selectedLink.setAttribute("aria-selected", false);
    const newLink = newItem.querySelector(".predictive-search__item");
    if (!newLink) return;
    newLink.setAttribute("aria-selected", true);
    this.input.setAttribute("aria-activedescendant", newLink.id);
    newLink.scrollIntoView({ block: "nearest" });
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"], [aria-selected="true"] button'
    );

    if (selectedProduct) selectedProduct.click();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onKeyup(event) {
    event.preventDefault();
    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
      case "Escape":
        this.closeSearch();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  onChange() {
    const searchTerm = this.input.value.trim();
    if (searchTerm.length < 3) {
      this.searchResultsWrapper.innerHTML = this.defaultText;
      return;
    }
    this.loader = this.querySelector(".predictive-search__search-template");
    this.predictiveSearchResults.classList.add("loading");
    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    fetch(
      `${window.routes.predictive_search_url}?q=${searchTerm}&resources[type]=product,page,article,query&resources[limit]=8&resources[options][fields]=title,product_type,variants.title,tag,body&section_id=predictive-search`
    )
      .then((response) => {
        if (!response.ok) {
          let error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        if (text) {
          this.searchResultsWrapper.innerHTML = text;
          this.predictiveSearchResults.classList.remove("loading");
          debounce(() => {
            this.trackSearch(searchTerm);
          }, 500).bind(this)(searchTerm);

          this.querySelectorAll(".predictive-search__item").forEach((n) => {
            n.addEventListener("click", () => {
              const resultData = {
                product_id: n.dataset.productId || null,
                product_title: n.dataset.productTitle || null,
                page_title: n.dataset.pageTitle || null,
              };
              this.trackSearch(searchTerm, resultData);
            });
          });
          this.predictiveSearchResults.classList.remove(
            "search-recommendation"
          );
          this.open();
        }
      })
      .catch((error) => {
        logger.error(error);
        this.close();
      });
  }

  open() {
    this.predictiveSearchResults.style.display = "block";
    this.setAttribute("open", true);
  }

  close() {
    this.predictiveSearchResults.style.display = "none";
  }
  trackSearch(searchTerm, resultData = {}) {
    if (searchTerm) {
      const dataCounts = this.getResultCount();
      const searchedData = {
        search_terms: searchTerm,
        product: resultData.product_title,
        productID: resultData.product_id,
        page_title: resultData.page_title,
        result_count: dataCounts.result_count,
        product_count: dataCounts.product_count,
        page_count: dataCounts.page_count,
      };
      layerPush({ event: "searched", searchedData: searchedData });
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);